/* ----------------------------------------------------------------------------
   VAUNI CONFIGURATOR ("Bygg din Globe")

   Companion element to the configurator snippet. Variant switching within a
   product is handled by the theme's own <variant-picker> (assets/theme.js).
   This element:
   - keeps summary rows, SKU, step-header values and price deltas fresh
   - keeps the color links pointing at the sibling variant matching the
     currently selected foot + liner
   - soft-swaps color changes (Section Rendering API) with an image-only
     crossfade, so the page never reloads and the controls never blink
   - crossfades the gallery on foot/liner changes (the theme cuts hard)
   - warms up sections and images the finger is about to choose
   - drives the mobile price dock (the theme's sticky bar, restyled) while
     the shopper is configuring
   - keeps the Charpstar 3D/AR buttons in step with the variant and brings
     them back after a soft swap
   - relocates the below-gallery content out of the .product grid so the
     sticky gallery never slides over it
   ---------------------------------------------------------------------------- */

function vauniFormatMoney(cents, format) {
  const amount = cents / 100;
  const replace = (decimals, thousands, decimalSep) => {
    const fixed = amount.toFixed(decimals);
    let [whole, fraction] = fixed.split(".");
    whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fraction ? `${whole}${decimalSep}${fraction}` : whole;
  };

  let value = String(Math.round(amount));

  if (format.includes("{{amount_no_decimals_with_comma_separator}}")) {
    value = replace(0, ".", ",");
  } else if (format.includes("{{amount_with_comma_separator}}")) {
    value = replace(2, ".", ",");
  } else if (format.includes("{{amount_no_decimals}}")) {
    value = replace(0, " ", ",");
  } else if (format.includes("{{amount}}")) {
    value = replace(2, " ", ",");
  }

  return format.replace(/\{\{\s*amount[a-z_]*\s*\}\}/, value);
}

/* Build a currency-correct formatter from a server-rendered sample of
   123456700 cents (= 1 234 567 in major units). The sample carries the active
   presentment currency's symbol, separators and decimal style, so client-side
   amounts stay correct on every market (SEK, EUR, NOK, GBP, USD, CAD, ...). */
function vauniFormatterFromSample(sample) {
  if (typeof sample !== "string") {
    return null;
  }

  const match = sample.match(/1(\D{0,2}?)234\1?567(?:(\D)00)?/);

  if (!match) {
    return null;
  }

  const thousands = match[1] || "";
  const decimalSep = match[2] || null;

  return (cents) => {
    const amount = cents / 100;
    let core;

    if (decimalSep) {
      const [whole, fraction] = amount.toFixed(2).split(".");
      core = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + decimalSep + fraction;
    } else {
      core = String(Math.round(amount)).replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    }

    return sample.replace(match[0], core);
  };
}

const vauniPrefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const vauniSavesData = () => navigator.connection?.saveData === true;

const vauniSleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const vauniEase = "cubic-bezier(0.25, 0.1, 0.25, 1)";

/* Section HTML, shared across swaps: url → Promise<string> ------------------ */

const vauniSectionCache = new Map();

function vauniSectionUrl(url, sectionId) {
  return `${url}${url.includes("?") ? "&" : "?"}section_id=${sectionId}`;
}

function vauniFetchSection(url, sectionId) {
  const key = vauniSectionUrl(url, sectionId);

  if (!vauniSectionCache.has(key)) {
    const request = fetch(key).then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      return response.text();
    });

    request.catch(() => vauniSectionCache.delete(key));
    vauniSectionCache.set(key, request);
  }

  return vauniSectionCache.get(key);
}

function vauniParseSection(html, sectionId) {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const section = doc.getElementById(`shopify-section-${sectionId}`) || doc.body;

  // DOMParser runs without scripting, so <noscript> content parses as real
  // elements — strip them or the fallback variant <select> becomes visible
  // and shadows the form's id input.
  section.querySelectorAll("noscript").forEach((node) => node.remove());

  return section;
}

/* Images ------------------------------------------------------------------- */

/* The hero of a (parsed or live) gallery: the variant's featured media cell */
function vauniHeroImage(scope) {
  return (
    scope?.querySelector(".product-gallery__media.is-initial img") ||
    scope?.querySelector(".product-gallery__media:not([hidden]) img") ||
    null
  );
}

/* Request an image exactly as the gallery would (same srcset + sizes → same
   cache entry), so the real element paints instantly when it appears. */
function vauniWarmImage(img, timeout = 900) {
  if (!img) {
    return Promise.resolve();
  }

  const warm = new Image();
  const sizes = img.getAttribute("sizes");
  const srcset = img.getAttribute("srcset");
  const src = img.currentSrc || img.getAttribute("src");

  if (sizes) warm.sizes = sizes;
  if (srcset) warm.srcset = srcset;
  if (src) warm.src = src;

  if (!warm.src && !warm.srcset) {
    return Promise.resolve();
  }

  const settled = warm.decode
    ? warm.decode().catch(() => {})
    : new Promise((resolve) => {
        warm.onload = warm.onerror = resolve;
      });

  // decode() may never resolve in background tabs — never wait past the timeout
  return Promise.race([settled, vauniSleep(timeout)]);
}

/* Wait until a live image has pixels on screen (or give up after the timeout) */
function vauniAwaitPainted(img, timeout = 1200) {
  if (!img) {
    return vauniSleep(40);
  }

  if (img.loading === "lazy") {
    img.loading = "eager";
  }

  if (img.complete && img.naturalWidth > 0) {
    return vauniSleep(40); // one frame to paint
  }

  const loaded = img.decode
    ? img.decode().catch(() => {})
    : new Promise((resolve) => {
        img.addEventListener("load", resolve, { once: true });
        img.addEventListener("error", resolve, { once: true });
      });

  return Promise.race([loaded, vauniSleep(timeout)]).then(() => vauniSleep(40));
}

/* The media cell currently in frame (the gallery is a horizontal scroller) */
function vauniVisibleMedia(gallery) {
  const carousel = gallery?.querySelector(".product-gallery__carousel");

  if (!carousel) {
    return null;
  }

  const cells = carousel.querySelectorAll(".product-gallery__media:not([hidden])");
  const edge = carousel.getBoundingClientRect().left;
  let best = null;
  let bestDistance = Infinity;

  cells.forEach((cell) => {
    const distance = Math.abs(cell.getBoundingClientRect().left - edge);

    if (distance < bestDistance) {
      best = cell;
      bestDistance = distance;
    }
  });

  return best;
}

/* A frozen copy of the frame on screen, fixed over its exact spot. The real
   gallery can change underneath; the old frame then dissolves away. */
function vauniGhost(cell) {
  const img = cell?.querySelector("img");

  if (!img || !img.currentSrc || !img.complete || img.naturalWidth === 0) {
    return null;
  }

  const rect = cell.getBoundingClientRect();

  if (rect.width < 10 || rect.height < 10 || rect.bottom <= 0 || rect.top >= window.innerHeight) {
    return null;
  }

  const cellStyle = getComputedStyle(cell);
  const imgStyle = getComputedStyle(img);
  const box = document.createElement("div");
  box.className = "vauni-cfg-ghost";
  box.setAttribute("aria-hidden", "true");
  Object.assign(box.style, {
    position: "fixed",
    left: `${rect.left}px`,
    top: `${rect.top}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
    borderRadius: cellStyle.borderRadius,
    overflow: "hidden",
    pointerEvents: "none",
    zIndex: "6",
    willChange: "opacity",
  });

  // Never paint over the sticky header
  const stickyArea =
    parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--sticky-area-height")) || 0;

  if (rect.top < stickyArea) {
    box.style.clipPath = `inset(${stickyArea - rect.top}px 0 0 0)`;
  }

  const copy = new Image();
  copy.alt = "";
  copy.decoding = "sync";
  Object.assign(copy.style, {
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: imgStyle.objectFit,
    objectPosition: imgStyle.objectPosition,
  });
  copy.src = img.currentSrc;
  box.append(copy);
  document.body.append(box);

  return box;
}

function vauniDissolve(box, duration = 360) {
  if (!box) {
    return;
  }

  let done = false;
  const finish = () => {
    if (!done) {
      done = true;
      box.remove();
    }
  };

  if (vauniPrefersReducedMotion() || !box.animate) {
    finish();
    return;
  }

  box.animate([{ opacity: 1 }, { opacity: 0 }], { duration, easing: vauniEase, fill: "forwards" })
    .finished.then(finish, finish);

  // Background tabs: animations may not tick — never leave a ghost behind
  setTimeout(finish, duration + 150);
}

/* Charpstar 3D/AR ------------------------------------------------------------
   The buttons are injected once at page load, keyed on the variant SKU, into
   the gallery. Keep them in step with the configuration and recreate them
   after a soft colour swap (which replaced the gallery they lived in). */

function vauniCharpstar() {
  return typeof charpstARVauni !== "undefined" &&
    charpstARVauni &&
    typeof charpstARVauni.checkLink === "function"
    ? charpstARVauni
    : null;
}

function vauniSyncCharpstar(sku) {
  const ar = vauniCharpstar();

  if (!ar || !sku) {
    return;
  }

  // Its desktop setup (QR modal + window listeners) must only ever run once;
  // the mobile branch restyles each new set of buttons and may repeat.
  if (typeof ar.setupMobileAndQR === "function" && !ar.setupMobileAndQR.vauniWrapped) {
    const original = ar.setupMobileAndQR;
    const wrapped = function () {
      const mobile =
        typeof this.isMobile === "function" ? this.isMobile() : /Mobi|Android/i.test(navigator.userAgent);

      if (mobile || !wrapped.ran) {
        wrapped.ran = true;
        return original.apply(this, arguments);
      }
    };
    wrapped.vauniWrapped = true;
    wrapped.ran = Boolean(ar.buttonsContainer);
    ar.setupMobileAndQR = wrapped;
  }

  const detached = Boolean(ar.buttonsContainer) && !ar.buttonsContainer.isConnected;

  if (ar.artNo === sku && !detached) {
    return;
  }

  if (detached) {
    ar.buttonsContainer = null;
    ar.arButton = null;
    ar.viewerButton = null;
  }

  ar.artNo = sku;
  ar.iOSLink = `https://vauni.charpstar.net/iOS/${sku}.usdz`;
  ar.androidLink = `https://vauni.charpstar.net/Android/${sku}.glb`;
  ar.checkLink(ar.androidLink);
}

/* ---------------------------------------------------------------------------- */

class VauniConfigurator extends HTMLElement {
  connectedCallback() {
    this.form = document.forms[this.getAttribute("form-id")];

    try {
      this.data = JSON.parse(this.querySelector("script[data-cfg-data]")?.textContent || "{}");
    } catch (error) {
      this.data = {};
    }

    // Color-swap clicks work in both modes (full configurator and color-only)
    this.onColorClick = this.onColorClick.bind(this);
    this.onIntent = this.onIntent.bind(this);
    this.addEventListener("click", this.onColorClick);

    // Warm up what the finger (or pointer) is about to choose
    this.addEventListener("touchstart", this.onIntent, { passive: true });
    this.addEventListener("pointerover", this.onIntent);
    this.addEventListener("focusin", this.onIntent);

    // The rest only applies to the full configurator (variant data present)
    if (!this.form || !Array.isArray(this.data.variants)) {
      return;
    }

    this.product = this.closest(".product");
    this.section =
      this.closest(".shopify-section") ||
      document.getElementById(`shopify-section-${this.getAttribute("section-id")}`);
    this.gallery = this.product?.querySelector(".product-gallery") || null;
    this.moneyFormat = window.themeVariables?.settings?.moneyFormat || "{{amount_no_decimals}} kr";
    this.formatFromSample = vauniFormatterFromSample(this.data.moneySample);
    this.onVariantChange = this.onVariantChange.bind(this);
    this.onOptionChange = this.onOptionChange.bind(this);
    this.form.addEventListener("variant:change", this.onVariantChange);
    this.addEventListener("change", this.onOptionChange);
    this.setupCampaign();

    // Keep the sticky gallery's travel zone free: the below-gallery content
    // (accordions, complementary products) moves out of the .product grid so
    // the pinned image can never slide over it.
    const below = this.product?.querySelector(".product-content-below-gallery");

    if (below) {
      this.product.after(below);
    }

    // Mobile: expose the pinned gallery's bottom edge as a CSS variable so
    // the step headers can stick right below the image.
    const gallery = this.gallery;

    if (gallery) {
      let lastOffset = null;
      let ticking = false;

      const measure = () => {
        ticking = false;

        if (window.matchMedia("(min-width: 1000px)").matches) {
          if (lastOffset !== null) {
            this.style.removeProperty("--vauni-cfg-pinned-bottom");
            lastOffset = null;
          }
          return;
        }

        // While pinned, the gallery's live bottom edge is the truth (the site
        // header hides/shows on scroll, shifting the sticky offset). Before it
        // pins, fall back to its future pinned position (sticky top + height).
        const rect = gallery.getBoundingClientRect();
        const stickyTop = parseFloat(getComputedStyle(gallery).top) || 0;
        const offset = Math.round(Math.min(rect.bottom, stickyTop + rect.height));

        if (offset !== lastOffset && offset > 0) {
          this.style.setProperty("--vauni-cfg-pinned-bottom", `${offset}px`);
          lastOffset = offset;
        }
      };

      this.updatePinnedOffset = () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(measure);
        }
      };

      measure();
      this.pinnedOffsetObserver = new ResizeObserver(this.updatePinnedOffset);
      this.pinnedOffsetObserver.observe(gallery);
      window.addEventListener("resize", this.updatePinnedOffset);
      window.addEventListener("scroll", this.updatePinnedOffset, { passive: true });
    }

    this.setupDock();

    // After a soft swap the gallery is new — bring the 3D/AR buttons back
    if (this.section?.hasAttribute("data-vauni-swapped")) {
      vauniSyncCharpstar(this.currentVariant()?.sku);
    }
  }

  disconnectedCallback() {
    this.form?.removeEventListener("variant:change", this.onVariantChange);

    if (this.onRerender) {
      this.form?.removeEventListener("product:rerender", this.onRerender);
    }

    this.removeEventListener("change", this.onOptionChange);
    this.removeEventListener("click", this.onColorClick);
    this.removeEventListener("touchstart", this.onIntent);
    this.removeEventListener("pointerover", this.onIntent);
    this.removeEventListener("focusin", this.onIntent);
    this.pinnedOffsetObserver?.disconnect();
    this.dockObserver?.disconnect();
    this.clearGhost();

    if (this.updatePinnedOffset) {
      window.removeEventListener("resize", this.updatePinnedOffset);
      window.removeEventListener("scroll", this.updatePinnedOffset);
    }
  }

  currentVariant() {
    return this.data.variants?.find((item) => item.id === this.data.currentVariantId) || null;
  }

  /* Mobile price dock ------------------------------------------------------
     The theme's sticky bar only appears once the whole form has scrolled
     past. While configuring, the price and the buy button are far below the
     steps — so on mobile the bar becomes a dock that is present whenever the
     top price has gone under the pinned image and the real button is not on
     screen. State lives on the section element (it survives re-renders). */

  setupDock() {
    if (
      !this.section ||
      !("IntersectionObserver" in window) ||
      !window.matchMedia("(max-width: 999px)").matches
    ) {
      return;
    }

    // The top price is the anchor: once it slides under the pinned image the
    // dock takes over. (The configurator header stands in if there is none.)
    const anchorFor = () =>
      this.section.querySelector('[data-block-type="price"]') || this.querySelector(".vauni-cfg__header");
    let anchor = anchorFor();
    const buy = this.section.querySelector('[data-block-type="buy-buttons"]');
    const footer = document.querySelector(".shopify-section--footer");

    if (!anchor || !buy || !this.section.querySelector(".product-sticky-bar")) {
      return;
    }

    if (this.data.dockLabel) {
      this.section.style.setProperty("--vauni-dock-label", JSON.stringify(this.data.dockLabel));
    }

    const state = { past: false, buyAway: true, footerAway: true };

    this.dockObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === anchor) {
            state.past =
              !entry.isIntersecting && entry.boundingClientRect.bottom < (entry.rootBounds?.top ?? 0);
          } else if (entry.target === buy) {
            state.buyAway = !entry.isIntersecting;
          } else if (entry.target === footer) {
            state.footerAway = !entry.isIntersecting;
          }
        });

        this.section.classList.toggle("vauni-dock-on", state.past && state.buyAway && state.footerAway);
      },
      // The top ~40% of the viewport is the pinned image: content under it is hidden
      { rootMargin: "-40% 0px 0px 0px" }
    );

    this.dockObserver.observe(anchor);
    this.dockObserver.observe(buy);

    if (footer) {
      this.dockObserver.observe(footer);
    }

    // The theme replaces the price block on every variant change — follow it
    this.onRerender = () => {
      const next = anchorFor();

      if (next && next !== anchor) {
        this.dockObserver.unobserve(anchor);
        anchor = next;
        this.dockObserver.observe(anchor);
      }
    };
    this.form.addEventListener("product:rerender", this.onRerender);
  }

  /* Intent: warm up before the tap lands ------------------------------------ */

  onIntent(event) {
    const target = event.target instanceof Element ? event.target : null;

    if (!target) {
      return;
    }

    const swatch = target.closest("[data-cfg-color]");

    if (swatch) {
      if (!swatch.classList.contains("is-selected")) {
        this.prefetchColor(swatch);
      }
      return;
    }

    const tile = target.closest("[data-cfg-foot], [data-cfg-liner]");

    if (tile) {
      this.preloadTileImage(tile);
    }
  }

  prefetchColor(link) {
    const sectionId = this.getAttribute("section-id");
    const url = link.getAttribute("href");

    if (vauniSavesData() || !sectionId || !url) {
      return;
    }

    const key = vauniSectionUrl(url, sectionId);

    vauniFetchSection(url, sectionId)
      .then((html) => {
        // Also warm the incoming hero so the dissolve lands on a painted frame
        if (!VauniConfigurator.warmed.has(key)) {
          VauniConfigurator.warmed.add(key);
          vauniWarmImage(vauniHeroImage(vauniParseSection(html, sectionId)), 4000);
        }
      })
      .catch(() => {});
  }

  preloadTileImage(tile) {
    const current = this.currentVariant();

    if (vauniSavesData() || !this.gallery || !current) {
      return;
    }

    const foot = tile.getAttribute("data-cfg-foot") ?? current.foot;
    const liner = tile.getAttribute("data-cfg-liner") ?? current.liner;
    const candidate = this.data.variants.find((item) => item.foot === foot && item.liner === liner);
    const img = candidate?.media
      ? this.gallery.querySelector(`[data-media-id="${candidate.media}"] img`)
      : null;

    if (img && img.loading === "lazy") {
      img.loading = "eager";
    }
  }

  /* Campaign — discount amount in the visitor's currency ------------------- */

  formatMoney(cents) {
    return this.formatFromSample ? this.formatFromSample(cents) : vauniFormatMoney(cents, this.moneyFormat);
  }

  setupCampaign() {
    const campaign = this.data.campaign;
    this.campaignCents = 0;

    if (!campaign) {
      return;
    }

    if (!campaign.convert) {
      this.campaignCents = Number(campaign.cents) || 0;
      return;
    }

    // Shopify converts fixed-amount discounts from the shop currency with the
    // same rate it exposes here, rounded to the cent (6 500 SEK → 588.79 EUR).
    const rate = parseFloat(window.Shopify?.currency?.rate);

    if (!(rate > 0) || !campaign.baseCents) {
      return; // keep the amount-free fallback label
    }

    this.campaignCents = Math.round(campaign.baseCents * rate);

    // Headline: a clean figure, rounded DOWN so we never promise more than the
    // checkout gives (588.79 → 580, 660.07 → 660, 6 330.22 → 6 300)
    const units = this.campaignCents / 100;
    const step = units >= 100 ? 10 ** (Math.floor(Math.log10(units)) - 1) : 1;
    const roundedCents = Math.floor(units / step) * step * 100;
    const roundedLabel = this.formatMoney(roundedCents).replace(/[.,]00(?=\D*$)/, "");

    const title = this.querySelector("[data-cfg-campaign-title]");

    if (title && campaign.titleTemplate) {
      title.textContent = campaign.titleTemplate.replace("[[amount]]", roundedLabel);
    }

    // Summary: exact, so it matches the checkout to the cent
    const campaignValue = this.querySelector("[data-cfg-summary-campaign]");

    if (campaignValue) {
      campaignValue.textContent = `−${this.formatMoney(this.campaignCents)}`;
    }

    const current = this.currentVariant();

    if (current) {
      const net = this.querySelector("[data-cfg-summary-net]");

      if (net) {
        net.textContent = this.formatMoney(Math.max(current.price - this.campaignCents, 0));
      }
    }

    this.querySelectorAll(".vauni-cfg__summary-row--campaign, .vauni-cfg__summary-row--net").forEach((row) => {
      row.hidden = false;
    });
  }

  /* Color change — soft swap with an image-only crossfade ------------------ */

  onColorClick(event) {
    const link = event.target.closest("[data-cfg-color]");

    if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    const sectionId = this.getAttribute("section-id");
    const container = sectionId && document.getElementById(`shopify-section-${sectionId}`);

    if (!container) {
      return; // fall back to normal navigation
    }

    event.preventDefault();

    if (link.classList.contains("is-selected") || this.swapping) {
      return;
    }

    this.softColorSwap(link, sectionId, container);
  }

  async softColorSwap(link, sectionId, container) {
    const url = link.getAttribute("href");
    let ghost = null;

    this.swapping = true;
    this.setAttribute("aria-busy", "true");
    link.classList.add("is-pending");

    try {
      const html = await vauniFetchSection(url, sectionId);
      const newSection = vauniParseSection(html, sectionId);

      // Warm the incoming hero so the dissolve lands on a painted frame
      await vauniWarmImage(vauniHeroImage(newSection), 900);

      const reduced = vauniPrefersReducedMotion();

      // An open 3D viewer lives inside the gallery we are about to replace
      const ar = vauniCharpstar();

      if (ar?.is3DViewerActive) {
        ar.hide3DViewerFromGallery?.();
      }

      // Freeze the frame on screen; everything else swaps in place underneath
      if (!reduced) {
        ghost = vauniGhost(vauniVisibleMedia(container.querySelector(".product-gallery")));
        const copy = ghost?.querySelector("img");

        if (copy?.decode) {
          await Promise.race([copy.decode().catch(() => {}), vauniSleep(80)]);
        }
      }

      const previousTitle = container.querySelector('[data-block-type="title"]')?.textContent.trim();
      const scrollTop = window.scrollY;

      // Flag first: the incoming configurator reads it while connecting
      container.dataset.vauniSwapped = "";
      container.replaceChildren(...newSection.childNodes);
      window.history.pushState({}, "", url);

      if (Math.abs(window.scrollY - scrollTop) > 1) {
        window.scrollTo(0, scrollTop);
      }

      if (!VauniConfigurator._popstateBound) {
        VauniConfigurator._popstateBound = true;
        window.addEventListener("popstate", () => window.location.reload());
      }

      // The section response carries no <title>; derive it from the product title
      const newTitle = container.querySelector('[data-block-type="title"]')?.textContent.trim();

      if (newTitle) {
        document.title = previousTitle && document.title.includes(previousTitle)
          ? document.title.replace(previousTitle, newTitle)
          : newTitle;
      }

      // Only what actually changed settles in: title, price, dock price
      if (!reduced) {
        container
          .querySelectorAll('[data-block-type="title"], [data-block-type="price"], .product-sticky-bar__info')
          .forEach((element) => {
            element.animate?.(
              [
                { opacity: 0.2, transform: "translateY(2px)" },
                { opacity: 1, transform: "none" },
              ],
              { duration: 300, delay: 60, easing: vauniEase, fill: "backwards" }
            );
          });
      }

      if (ghost) {
        // The new gallery lands on its featured cell; dissolve once it has painted
        const gallery = container.querySelector(".product-gallery");
        await vauniSleep(30);
        await vauniAwaitPainted(
          vauniHeroImage(gallery) || vauniVisibleMedia(gallery)?.querySelector("img"),
          900
        );
        vauniDissolve(ghost);
        ghost = null;
      }

      window.Shopify?.PaymentButton?.init?.();
    } catch (error) {
      // Graceful fallback: do the normal navigation instead
      ghost?.remove();
      window.location.href = url;
    } finally {
      this.swapping = false;
      this.removeAttribute("aria-busy");
      link.classList.remove("is-pending");
    }
  }

  /* Same-product variant change ------------------------------------------- */

  onOptionChange(event) {
    const input = event.target;

    if (!(input instanceof HTMLInputElement) || !input.hasAttribute("data-option-position")) {
      return;
    }

    if (vauniPrefersReducedMotion()) {
      return;
    }

    // Freeze the current frame now; the theme will cut the carousel to the
    // new variant's image, and we dissolve from the old one to it.
    this.clearGhost();
    this.ghost = vauniGhost(vauniVisibleMedia(this.gallery));

    if (this.ghost) {
      this.ghostTimer = setTimeout(() => this.clearGhost(true), 2500);
    }
  }

  clearGhost(dissolve = false) {
    clearTimeout(this.ghostTimer);
    const ghost = this.ghost;
    this.ghost = null;

    if (!ghost) {
      return;
    }

    if (dissolve) {
      vauniDissolve(ghost);
    } else {
      ghost.remove();
    }
  }

  settleGhost() {
    const ghost = this.ghost;

    if (!ghost) {
      return;
    }

    clearTimeout(this.ghostTimer);
    this.ghost = null;

    // Give the carousel a frame to land on the new cell, then wait for its image
    setTimeout(async () => {
      await vauniAwaitPainted(vauniVisibleMedia(this.gallery)?.querySelector("img"), 1200);
      vauniDissolve(ghost);
    }, 30);
  }

  onVariantChange(event) {
    const variant = event.detail?.variant;

    if (!variant) {
      return;
    }

    this.data.currentVariantId = variant.id;

    const options = variant.options || [];
    const foot = (options[1] || "").trim();
    const liner = (options[2] || "").trim();
    const current = this.data.variants.find((item) => item.foot === foot && item.liner === liner);

    // Summary rows + step header values (with a subtle settle-in crossfade)
    const reducedMotion = vauniPrefersReducedMotion();
    const setValue = (selector, text) => {
      const element = this.querySelector(selector);

      if (!element || element.textContent === text) {
        return;
      }

      element.replaceChildren(text);

      if (!reducedMotion) {
        element.animate?.(
          [
            { opacity: 0.2, transform: "translateY(2px)" },
            { opacity: 1, transform: "none" },
          ],
          { duration: 240, easing: vauniEase }
        );
      }
    };

    const footLabel = this.data.footLabels?.[foot] || foot;
    const linerLabel = liner.replace(/\s+liner$/i, "");
    setValue("[data-cfg-summary-foot]", footLabel);
    setValue("[data-cfg-summary-liner]", linerLabel);
    setValue("[data-cfg-summary-sku]", variant.sku || current?.sku || "");
    setValue("[data-cfg-step-foot]", footLabel);
    setValue("[data-cfg-step-liner]", linerLabel);

    // Total price in the spec sheet
    const totalCents = current?.price ?? variant.price;

    if (typeof totalCents === "number") {
      setValue("[data-cfg-summary-price]", this.formatMoney(totalCents));

      // Campaign: price after the automatic discount that is applied at checkout
      if (this.campaignCents > 0) {
        setValue("[data-cfg-summary-net]", this.formatMoney(Math.max(totalCents - this.campaignCents, 0)));
      }
    }

    // Price deltas relative to the newly selected variant
    const basePrice = current?.price ?? variant.price;

    this.querySelectorAll("[data-cfg-foot]").forEach((tile) => {
      const candidate = this.data.variants.find(
        (item) => item.foot === tile.getAttribute("data-cfg-foot") && item.liner === liner
      );
      this.renderDelta(tile, candidate, basePrice);
    });

    this.querySelectorAll("[data-cfg-liner]").forEach((tile) => {
      const candidate = this.data.variants.find(
        (item) => item.liner === tile.getAttribute("data-cfg-liner") && item.foot === foot
      );
      this.renderDelta(tile, candidate, basePrice);
    });

    // Keep the color links pointing at the sibling variant that matches the
    // currently selected foot + liner (matched by option position index).
    const footIndex = this.data.footValues?.indexOf(foot) ?? -1;
    const linerIndex = this.data.linerValues?.indexOf(liner) ?? -1;

    if (footIndex >= 0 && linerIndex >= 0 && Array.isArray(this.data.siblings)) {
      this.querySelectorAll("[data-cfg-color]").forEach((link) => {
        const sibling = this.data.siblings.find((item) => item.handle === link.getAttribute("data-cfg-color"));
        const variantId = sibling?.ids?.[footIndex]?.[linerIndex];

        if (variantId) {
          link.setAttribute("href", `${sibling.url.split("?")[0]}?variant=${variantId}`);
        }
      });
    }

    // Price micro-animation (the theme has just re-rendered the price block)
    if (!reducedMotion) {
      document
        .querySelectorAll('[data-block-type="price"], .product-sticky-bar__info')
        .forEach((element) => {
          element.animate?.(
            [
              { opacity: 0.25, transform: "translateY(2px)" },
              { opacity: 1, transform: "none" },
            ],
            { duration: 320, easing: "ease-out" }
          );
        });
    }

    // The theme has cut the gallery to the new image — dissolve the old frame
    this.settleGhost();

    // 3D/AR model for this exact variant
    vauniSyncCharpstar(variant.sku || current?.sku);
  }

  renderDelta(tile, candidate, basePrice) {
    const deltaElement = tile.querySelector("[data-cfg-delta]");

    if (!deltaElement) {
      return;
    }

    if (candidate && candidate.price !== basePrice) {
      const diff = candidate.price - basePrice;
      const sign = diff > 0 ? "+" : "−";
      deltaElement.textContent = `${sign}${this.formatMoney(Math.abs(diff))}`;
    } else {
      deltaElement.textContent = "";
    }
  }
}

VauniConfigurator.warmed = new Set();

/* The theme's gallery navigation keeps observing cells of a gallery that the
   soft color-swap has just replaced; its `carousel` getter then resolves to
   null and throws inside the observer callback. Resolve to an inert stand-in
   instead — the observer bails out on `isScrollable` and nothing else runs. */
customElements.whenDefined("product-gallery-navigation").then((NavigationClass) => {
  let proto = NavigationClass.prototype;

  while (proto && !Object.getOwnPropertyDescriptor(proto, "carousel")) {
    proto = Object.getPrototypeOf(proto);
  }

  const descriptor = proto && Object.getOwnPropertyDescriptor(proto, "carousel");

  if (!descriptor?.get || descriptor.get.vauniPatched) {
    return;
  }

  const inertCarousel = { isScrollable: true, allCells: [], cells: [], scrollTo() {}, select() {} };
  const getCarousel = function () {
    return descriptor.get.call(this) || inertCarousel;
  };
  getCarousel.vauniPatched = true;

  Object.defineProperty(proto, "carousel", { ...descriptor, get: getCarousel });
}).catch(() => {});

if (!window.customElements.get("vauni-configurator")) {
  window.customElements.define("vauni-configurator", VauniConfigurator);
}
